import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import * as L from "leaflet";
import { useImportedLayers } from "../../../context/ImportedLayersContext";
import { useLayers } from "../../../context/LayersContext";
import { mockLayers } from "../../../global/constants/layers";

import {
  Ruler,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  ZoomIn,
  Download,
} from "../../ui/icons";
import { cn } from "../../../lib/utils";

import {
  useRulerModalContext,
  DrawingType,
  MAP_TOOLS,
} from "../../../context/RulerContext";
import {
  MapPinned,
  PencilLine,
  Pentagon,
  Check,
  FileText,
  FilePlus,
  FileDown,
  Mountain,
  FileUp,
  FileArchive,
  Loader2,
} from "lucide-react";
import Typography from "../../Typography";
import {
  AREA_CONVERSION_FACTORS,
  DISTANCE_CONVERSION_FACTORS,
} from "./measurementConversions";
import {
  formatAreaByUnit,
  formatAreaNumber,
  computeAreaKm2,
} from "../../../utils/areaFormatting";
import {
  isAreaExportable,
  AREA_EXPORT_MAX_AREA_HA,
} from "../../../utils/areaExport";
import type { Feature } from "geojson";
import FileUpload from "../FileUpload";
import ProfileChart from "../ProfileChart";

interface ToolsMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onAreaConfirmed?: (geometry: any, id: string) => void;
  onDrawingHover?: (id: string | null) => void;
  onDrawingSelect?: (id: string | null) => void;
  onImportedLayerSelect?: (layerId: string | null) => Promise<void>;
  onReportDrawing?: (drawingId: string) => void;
  onExportArea?: (drawingId: string) => void;
  exportingDrawingId?: string | null;
  selectedDrawingId?: string | null;
  hoveredDrawingId?: string | null;
}

interface Tool {
  id: DrawingType;
  label: string;
  icon: React.ReactElement;
}

export const ToolsMenu = ({
  isOpen,
  onClose,
  onAreaConfirmed,
  onDrawingHover,
  onDrawingSelect,
  onImportedLayerSelect,
  onReportDrawing,
  onExportArea,
  exportingDrawingId,
  selectedDrawingId: externalSelectedDrawingId,
  hoveredDrawingId,
}: ToolsMenuProps) => {
  const {
    importedLayers,
    removeImportedLayer,
    toggleImportedLayerVisibility,
    getLeafletLayer,
  } = useImportedLayers();

  const {
    activeFunctionHandler,
    activeFunction,
    drawings,
    removeDrawing,
    toggleDrawingVisibility,
    updateDrawingName,
    downloadGeoJSON,
    setDrawings,
    measureResult,
    isFinished,
    isConfirmed,
    cancelMeasure,
    confirmMeasure,
    profileData,
    isLoadingProfile,
    profileLayerName,
    clearProfile,
  } = useRulerModalContext();

  const { layers = [] } = useLayers();
  const hasActiveRaster = layers.some((title: string) =>
    mockLayers.find((l) => l.title === title && l.type === "raster")
  );

  const showProfile = isLoadingProfile || profileData !== null;

  const menuRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const discardRef = useRef<HTMLButtonElement>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [tempName, setTempName] = useState("");
  const [selectedDrawingId, setSelectedDrawingId] = useState<string | null>(
    null
  );
  const [selectedImportedLayerId, setSelectedImportedLayerId] = useState<
    string | null
  >(null);
  const prevDrawingsLength = useRef(drawings.length);

  useEffect(() => {
    if (externalSelectedDrawingId === undefined) return;
    setSelectedDrawingId(externalSelectedDrawingId);
  }, [externalSelectedDrawingId]);

  useEffect(() => {
    if (drawings.length > prevDrawingsLength.current) {
      const newDrawing = drawings[drawings.length - 1];

      if (newDrawing) {
        setSelectedDrawingId(newDrawing.id);
        setSelectedImportedLayerId(null);
        if (onDrawingSelect) {
          onDrawingSelect(newDrawing.id);
        }

        if (
          newDrawing.type === MAP_TOOLS.MEASURE_AREA &&
          onAreaConfirmed &&
          newDrawing.layer
        ) {
          // @ts-ignore
          const geoJson = newDrawing.layer.toGeoJSON();
          onAreaConfirmed(geoJson.geometry, newDrawing.id);
        }
      }
    }
    prevDrawingsLength.current = drawings.length;
  }, [drawings, onAreaConfirmed, onDrawingSelect]);

  // Sempre recalcula do GeoJSON para MEASURE_AREA — garante consistência com as estatísticas.
  const getDrawingAreaM2 = (drawing: (typeof drawings)[0]): number => {
    if (drawing.type !== MAP_TOOLS.MEASURE_AREA || !drawing.layer) {
      return drawing.rawValue ?? 0;
    }
    const geoJsonFeature = (drawing.layer as L.Polygon).toGeoJSON() as Feature;
    return geoJsonFeature.geometry
      ? computeAreaKm2(geoJsonFeature.geometry) * 1_000_000
      : drawing.rawValue ?? 0;
  };

  const convertDistance = (meters: number, toUnit: string): string => {
    const {
      FEET_PER_METER,
      METERS_PER_KILOMETER,
      METERS_PER_MILE,
      YARDS_PER_METER,
    } = DISTANCE_CONVERSION_FACTORS;

    switch (toUnit) {
      case "m":
        return `${meters.toFixed(2)} m`;
      case "km":
        return `${(meters / METERS_PER_KILOMETER).toFixed(2)} km`;
      case "mi":
        return `${(meters / METERS_PER_MILE).toFixed(2)} mi`;
      case "ft":
        return `${(meters * FEET_PER_METER).toFixed(2)} ft`;
      case "yd":
        return `${(meters * YARDS_PER_METER).toFixed(2)} yd`;
      default:
        return `${(meters / METERS_PER_KILOMETER).toFixed(2)} km`;
    }
  };

  const convertArea = (squareMeters: number, toUnit: string): string => {
    const {
      SQUARE_FEET_PER_SQUARE_METER,
      SQUARE_METERS_PER_ACRE,
      SQUARE_METERS_PER_HECTARE,
      SQUARE_METERS_PER_SQUARE_KILOMETER,
      SQUARE_METERS_PER_SQUARE_MILE,
    } = AREA_CONVERSION_FACTORS;

    switch (toUnit) {
      case "km²":
        return formatAreaByUnit(
          squareMeters / SQUARE_METERS_PER_SQUARE_KILOMETER,
          "km²"
        );
      case "ha":
        return formatAreaByUnit(squareMeters / SQUARE_METERS_PER_HECTARE, "ha");
      case "acres":
        return formatAreaByUnit(squareMeters / SQUARE_METERS_PER_ACRE, "acres");
      case "mi²":
        return formatAreaByUnit(
          squareMeters / SQUARE_METERS_PER_SQUARE_MILE,
          "mi²"
        );
      case "ft²":
        return formatAreaByUnit(
          squareMeters * SQUARE_FEET_PER_SQUARE_METER,
          "ft²"
        );
      case "m²":
      default:
        return formatAreaByUnit(squareMeters, "m²");
    }
  };

  const getMeasurementValue = (
    type: DrawingType,
    rawValue: number,
    unit?: string
  ): string => {
    const {
      FEET_PER_METER,
      METERS_PER_KILOMETER,
      METERS_PER_MILE,
      YARDS_PER_METER,
    } = DISTANCE_CONVERSION_FACTORS;
    const {
      SQUARE_FEET_PER_SQUARE_METER,
      SQUARE_METERS_PER_ACRE,
      SQUARE_METERS_PER_HECTARE,
      SQUARE_METERS_PER_SQUARE_KILOMETER,
      SQUARE_METERS_PER_SQUARE_MILE,
    } = AREA_CONVERSION_FACTORS;

    if (type === MAP_TOOLS.MEASURE) {
      switch (unit) {
        case "m":
          return rawValue.toFixed(2);
        case "mi":
          return (rawValue / METERS_PER_MILE).toFixed(2);
        case "ft":
          return (rawValue * FEET_PER_METER).toFixed(2);
        case "yd":
          return (rawValue * YARDS_PER_METER).toFixed(2);
        case "km":
        default:
          return (rawValue / METERS_PER_KILOMETER).toFixed(2);
      }
    }

    if (type === MAP_TOOLS.MEASURE_AREA) {
      const normalizedUnit = unit?.replace(/\u00c2/g, "") ?? "m²";

      switch (normalizedUnit) {
        case "km²":
          return formatAreaNumber(
            rawValue / SQUARE_METERS_PER_SQUARE_KILOMETER,
            "km²"
          );
        case "ha":
          return formatAreaNumber(rawValue / SQUARE_METERS_PER_HECTARE, "ha");
        case "acres":
          return formatAreaNumber(rawValue / SQUARE_METERS_PER_ACRE, "acres");
        case "mi²":
          return formatAreaNumber(
            rawValue / SQUARE_METERS_PER_SQUARE_MILE,
            "mi²"
          );
        case "ft²":
          return formatAreaNumber(
            rawValue * SQUARE_FEET_PER_SQUARE_METER,
            "ft²"
          );
        case "m²":
        default:
          return formatAreaNumber(rawValue, "m²");
      }
    }

    return "";
  };

  const changeUnit = (drawingId: string, newUnit: string) => {
    setDrawings(
      drawings.map((d) => {
        if (d.id === drawingId && d.rawValue !== undefined) {
          let newResult = "";
          if (d.type === MAP_TOOLS.MEASURE) {
            newResult = convertDistance(d.rawValue, newUnit);
          } else if (d.type === MAP_TOOLS.MEASURE_AREA) {
            newResult = convertArea(getDrawingAreaM2(d), newUnit);
          }
          return { ...d, unit: newUnit, result: newResult };
        }
        return d;
      })
    );
  };

  const tools: Tool[] = [
    { id: "LOCATION", label: "Localizar", icon: <MapPinned size={20} /> },
    { id: "MEASURE", label: "Medir distância", icon: <Ruler size={20} /> },
    { id: "MEASURE_AREA", label: "Definir área", icon: <Pentagon size={20} /> },
    { id: "DRAW", label: "Desenhar", icon: <PencilLine size={20} /> },
    { id: "PROFILE", label: "Traçar perfil", icon: <Mountain size={20} /> },
    {
      id: "UPLOAD",
      label: "Enviar arquivo",
      icon: <FileUp size={20} />,
    },
  ];

  const getDrawingIcon = (type: DrawingType) => {
    switch (type) {
      case MAP_TOOLS.LOCATION:
        return <MapPinned className="w-5.25 h-5.25" />;
      case MAP_TOOLS.MEASURE:
        return <Ruler className="w-5.25 h-5.25" />;
      case MAP_TOOLS.MEASURE_AREA:
        return <Pentagon className="w-5.25 h-5.25" />;
      case MAP_TOOLS.DRAW:
        return <PencilLine className="w-5.25 h-5.25" />;
      case MAP_TOOLS.UPLOAD:
        return <FilePlus className="w-5.25 h-5.25" />;
      case MAP_TOOLS.PROFILE:
        return <Mountain className="w-5.25 h-5.25" />;
    }
  };

  useEffect(() => {
    if (isOpen && menuRef.current) {
      L.DomEvent.disableClickPropagation(menuRef.current);
      L.DomEvent.disableScrollPropagation(menuRef.current);
    }
  }, [isOpen]);

  const handleMouseEnter = (drawing: any) => {
    if (onDrawingHover) {
      onDrawingHover(drawing.id);
    }

    if (!drawing.isVisible || !drawing.layer) return;

    const layer = drawing.layer;
    if (layer instanceof L.Marker) {
      const marker = layer as any;
      const icon = marker._icon;
      const shadow = marker._shadow;
      if (icon) {
        const originalTransform = icon.style.transform || "";
        const computedTransform = window.getComputedStyle(icon).transform;
        const baseTransform =
          computedTransform !== "none" ? computedTransform : originalTransform;
        icon.style.filter =
          "brightness(1.3) drop-shadow(0 0 8px #FFD700) drop-shadow(0 0 12px #FFD700)";
        icon.style.transform = baseTransform + " scale(1.15)";
        icon.style.zIndex = "1000";
        icon.dataset.originalTransform = baseTransform;
      }
      if (shadow) {
        const originalTransform = shadow.style.transform || "";
        const computedTransform = window.getComputedStyle(shadow).transform;
        const baseTransform =
          computedTransform !== "none" ? computedTransform : originalTransform;
        shadow.style.filter = "brightness(0.8)";
        shadow.style.transform = baseTransform + " scale(1.15)";
        shadow.dataset.originalTransform = baseTransform;
      }
    } else if (layer instanceof L.Path) {
      layer.setStyle({ color: "#FFD700", weight: 6, opacity: 1 });
    }
  };

  const handleMouseLeave = (drawing: any) => {
    if (!drawing.layer) return;
    const layer = drawing.layer;
    if (layer instanceof L.Marker) {
      const marker = layer as any;
      const icon = marker._icon;
      const shadow = marker._shadow;
      if (icon) {
        icon.style.filter = "";
        icon.style.transform = icon.dataset.originalTransform || "";
        icon.style.zIndex = "";
        delete icon.dataset.originalTransform;
      }
      if (shadow) {
        shadow.style.filter = "";
        shadow.style.transform = shadow.dataset.originalTransform || "";
        delete shadow.dataset.originalTransform;
      }
    } else if (layer instanceof L.Path) {
      layer.setStyle({ color: "#0C2856", weight: 4, opacity: 1 });
    }
  };

  const handleDrawingClick = (drawing: any) => {
    if (selectedDrawingId === drawing.id) {
      setSelectedDrawingId(null);
      setSelectedImportedLayerId(null);
      if (onDrawingSelect) onDrawingSelect(null);
      return;
    }
    setSelectedDrawingId(drawing.id);
    setSelectedImportedLayerId(null);
    if (onDrawingSelect) onDrawingSelect(drawing.id);
  };

  const handleListKeyDown = (e: React.KeyboardEvent) => {
    if (!["ArrowDown", "ArrowUp"].includes(e.key)) return;
    e.preventDefault();
    const currentIndex = drawings.findIndex((d) => d.id === selectedDrawingId);
    let nextIndex = currentIndex;
    if (e.key === "ArrowDown")
      nextIndex = Math.min(currentIndex + 1, drawings.length - 1);
    if (e.key === "ArrowUp") nextIndex = Math.max(currentIndex - 1, 0);
    if (nextIndex !== currentIndex) {
      const next = drawings[nextIndex];
      setSelectedDrawingId(next.id);
      if (onDrawingSelect) onDrawingSelect(next.id);
    }
  };

  useEffect(() => {
    if (isFinished && !isConfirmed) {
      setTimeout(() => confirmRef.current?.focus(), 0);
    }
  }, [isFinished, isConfirmed]);

  const handleConfirmKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Tab") {
      e.preventDefault();
      discardRef.current?.focus();
    }
  };

  const handleDiscardKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Tab") {
      e.preventDefault();
      confirmRef.current?.focus();
    }
  };

  const handleImportedLayerClick = (layerId: string) => {
    setSelectedImportedLayerId(layerId);
    setSelectedDrawingId(null);
    if (onImportedLayerSelect) {
      onImportedLayerSelect(layerId);
    }
  };

  const handleZoomToImportedLayer = (layerId: string) => {
    handleImportedLayerClick(layerId);

    let map: L.Map | null = null;
    const leafletLayer = getLeafletLayer(layerId);
    if (leafletLayer && (leafletLayer as any)._map)
      map = (leafletLayer as any)._map;
    if (!map) {
      const mapContainer = document.getElementById("main-map");
      if (mapContainer) {
        if ((mapContainer as any)._leaflet_map)
          map = (mapContainer as any)._leaflet_map;
        else {
          const internal = mapContainer.querySelector(".leaflet-container");
          if (internal && (internal as any)._leaflet_map)
            map = (internal as any)._leaflet_map;
        }
      }
    }
    if (!map) return;

    try {
      // L.KML (FeatureGroup) and L.GeoJSON both have getBounds()
      if (leafletLayer) {
        const bounds = (leafletLayer as L.FeatureGroup).getBounds();
        if (bounds.isValid()) {
          map.fitBounds(bounds, {
            padding: [100, 100],
            animate: true,
            duration: 1.5,
          });
          return;
        }
      }
      // Fallback to stored bbox (useful if layer ref not yet registered)
      const il = importedLayers.find((l) => l.id === layerId);
      if (il?.bbox) {
        const [minLng, minLat, maxLng, maxLat] = il.bbox;
        const bounds = L.latLngBounds([minLat, minLng], [maxLat, maxLng]);
        if (bounds.isValid()) {
          map.fitBounds(bounds, {
            padding: [100, 100],
            animate: true,
            duration: 1.5,
          });
        }
      }
    } catch (error) {
      console.error("Erro ao tentar dar zoom na camada importada:", error);
    }
  };

  const handleImportedLayerMouseEnter = (layerId: string) => {
    const leafletLayer = getLeafletLayer(layerId);
    if (!leafletLayer) return;
    try {
      (leafletLayer as L.GeoJSON).setStyle({ color: "#FFD700", weight: 4 });
    } catch {
      // layer may not support setStyle
    }
  };

  const handleImportedLayerMouseLeave = (layerId: string) => {
    const leafletLayer = getLeafletLayer(layerId);
    if (!leafletLayer) return;
    try {
      (leafletLayer as L.GeoJSON).setStyle({
        color: "#2E7D32",
        weight: 2,
        opacity: 0.8,
      });
    } catch {
      // layer may not support setStyle
    }
  };

  const handleDeleteImportedLayer = (e: React.MouseEvent, layerId: string) => {
    e.stopPropagation();
    if (selectedImportedLayerId === layerId) {
      setSelectedImportedLayerId(null);
      if (onImportedLayerSelect) {
        onImportedLayerSelect(null);
      }
    }
    removeImportedLayer(layerId);
  };

  const handleZoomToLayer = (drawing: any) => {
    handleDrawingClick(drawing);

    if (!drawing.layer) return;
    let map: L.Map | null = null;
    if ((drawing.layer as any)._map) map = (drawing.layer as any)._map;
    if (!map) {
      const mapContainer = document.getElementById("main-map");
      if (mapContainer) {
        if ((mapContainer as any)._leaflet_map)
          map = (mapContainer as any)._leaflet_map;
        else {
          const internal = mapContainer.querySelector(".leaflet-container");
          if (internal && (internal as any)._leaflet_map)
            map = (internal as any)._leaflet_map;
        }
      }
    }
    if (!map) return;
    try {
      if (
        drawing.layer instanceof L.Polygon ||
        drawing.layer instanceof L.Polyline ||
        drawing.layer instanceof L.Circle ||
        drawing.layer instanceof L.FeatureGroup
      ) {
        const bounds = drawing.layer.getBounds();
        if (bounds.isValid()) {
          map.fitBounds(bounds, {
            padding: [100, 100],
            animate: true,
            duration: 1.5,
          });
        }
      } else if (
        drawing.layer instanceof L.Marker ||
        drawing.layer instanceof L.CircleMarker
      ) {
        const latlng = drawing.layer.getLatLng();
        if (latlng) {
          map.flyTo(latlng, 16, { animate: true, duration: 1.5 });
        }
      }
    } catch (error) {
      console.error("Erro ao tentar dar zoom:", error);
    }
  };

  const startEditing = (id: string, currentName: string) => {
    setEditingId(id);
    setTempName(currentName);
  };
  const saveEditing = (id: string) => {
    if (tempName.trim() !== "") updateDrawingName(id, tempName);
    setEditingId(null);
  };
  const cancelEditing = () => {
    setEditingId(null);
  };
  const handleConfirmMeasure = () => {
    confirmMeasure();
    activeFunctionHandler(null);
  };

  const handleDeleteDrawing = (e: React.MouseEvent, idToDelete: string) => {
    e.stopPropagation();

    const remainingDrawings = drawings.filter((d) => d.id !== idToDelete);

    if (remainingDrawings.length > 0) {
      const nextToShow = remainingDrawings[remainingDrawings.length - 1];
      setSelectedDrawingId(nextToShow.id);
      if (onDrawingSelect) {
        onDrawingSelect(nextToShow.id);
      }
    } else {
      setSelectedDrawingId(null);
      if (onDrawingSelect) {
        onDrawingSelect(null);
      }
    }

    removeDrawing(idToDelete);
  };

  if (!isOpen && activeFunction) activeFunctionHandler(null);
  if (!isOpen && !activeFunction) return null;

  return createPortal(
    <div
      ref={menuRef}
      data-tour="tools-menu"
      role="dialog"
      aria-label="Ferramentas do Mapa"
      className={cn(
        "fixed top-[145px] z-[9999] bg-white rounded-md shadow-lg w-[400px] flex flex-col",
        "transition-[left] duration-300 ease-in-out pointer-events-auto",
        "left-4 max-h-2/3 overflow-y-auto"
      )}
    >
      <div
        className={cn(
          "flex flex-col",
          "py-4",
          drawings.length > 0 && "border-b border-gray-100"
        )}
      >
        <div className="flex items-center justify-between mb-4 px-4">
          <Typography variant="subtitle1" color="#080D1A">
            Ferramentas do Mapa
          </Typography>
        </div>
        <div className="grid grid-cols-3 gap-2 px-4">
          {tools.map((tool) => {
            const isActive = activeFunction === tool.id;
            const isProfileDisabled = tool.id === "PROFILE" && !hasActiveRaster;
            return (
              <button
                key={tool.id}
                disabled={isProfileDisabled}
                onClick={() => {
                  if (isActive) {
                    activeFunctionHandler(null);
                  } else {
                    activeFunctionHandler(tool.id as any);
                  }
                }}
                className={cn(
                  "flex flex-col items-center justify-center gap-2 py-3 px-2 rounded-xl border transition-all duration-200 bg-gray-50 text-gray-600 ",
                  isActive
                    ? "bg-primary text-white border-primary shadow-md"
                    : isProfileDisabled
                    ? "bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed"
                    : "bg-gray-50 text-gray-600 border-gray-100 hover:bg-primary/5 hover:border-primary/30 hover:text-primary"
                )}
                aria-label={tool.label}
                title={
                  isProfileDisabled
                    ? "Selecione uma camada raster para traçar o perfil"
                    : tool.label
                }
              >
                {tool.icon}
                <span className="text-[11px] font-medium leading-tight text-center">
                  {tool.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {activeFunction === "UPLOAD" && (
        <div className="border-t py-4">
          <Typography variant="body2" className="px-5 mb-3 text-gray-600">
            Importe seu arquivo em GeoJSON, KML, KMZ ou Shapefile.
          </Typography>
          <FileUpload />
        </div>
      )}

      {showProfile && (
        <div className="border-t border-gray-100 my-2">
          <div className="flex items-center justify-between px-5 pt-4 pb-2">
            <div className="flex items-center gap-2">
              <Mountain className="w-4 h-4 text-[#E07B39]" />
              <h4 className="text-sm font-semibold text-gray-600">
                Perfil{profileLayerName ? ` · ${profileLayerName}` : ""}
              </h4>
            </div>
            <button
              onClick={clearProfile}
              title="Remover perfil"
              className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
            >
              <Trash2 className="w-4 h-4 text-red-400" />
            </button>
          </div>
          <div className="px-3 pb-3">
            <ProfileChart
              data={profileData ?? []}
              layerName={profileLayerName ?? ""}
              isLoading={isLoadingProfile}
            />
          </div>
        </div>
      )}

      {drawings.length > 0 && (
        <div className="flex flex-col">
          <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-b border-gray-100">
            <h4 className="text-sm font-semibold text-gray-600">
              Marcações ({drawings.length})
            </h4>
            <button
              onClick={downloadGeoJSON}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-primary hover:bg-primary/10 rounded-lg transition-colors text-xs font-medium"
            >
              <Download className="w-3.5 h-3.5" />
              GeoJSON
            </button>
          </div>
          <div className="px-4 py-3" onKeyDown={handleListKeyDown}>
            {drawings.map((drawing) => (
              <div
                key={drawing.id}
                tabIndex={0}
                className={cn(
                  "flex items-start gap-3 p-3 mb-2 rounded-xl cursor-pointer outline-none",
                  "transition-all duration-200",
                  selectedDrawingId === drawing.id
                    ? "bg-blue-50 border border-blue-200"
                    : hoveredDrawingId === drawing.id
                    ? "bg-yellow-50 border border-yellow-300"
                    : drawing.isVisible
                    ? "bg-gray-50 hover:bg-gray-100 border border-transparent"
                    : "bg-gray-100 opacity-50 border border-transparent"
                )}
                onClick={() => handleDrawingClick(drawing)}
                onMouseEnter={() => handleMouseEnter(drawing)}
                onMouseLeave={() => handleMouseLeave(drawing)}
              >
                <div className="text-primary mt-0.5">
                  {getDrawingIcon(drawing.type)}
                </div>
                <div className="flex-1 min-w-0">
                  {editingId === drawing.id ? (
                    <input
                      type="text"
                      value={tempName}
                      onChange={(e) => setTempName(e.target.value)}
                      onBlur={() => saveEditing(drawing.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveEditing(drawing.id);
                        if (e.key === "Escape") cancelEditing();
                      }}
                      className="w-full px-2 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <>
                      <div className="flex items-center gap-1.5 mb-1">
                        <span
                          className="text-sm font-medium text-gray-800 truncate"
                          title={drawing.name}
                        >
                          {drawing.name}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditing(drawing.id, drawing.name);
                          }}
                          className="p-1 hover:bg-gray-200 rounded transition-colors"
                        >
                          <Pencil className="w-3 h-3 text-gray-400" />
                        </button>
                      </div>
                      {drawing.result && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600 font-medium">
                            {(drawing.type === MAP_TOOLS.MEASURE ||
                              drawing.type === MAP_TOOLS.MEASURE_AREA) &&
                            drawing.rawValue !== undefined
                              ? getMeasurementValue(
                                  drawing.type,
                                  drawing.type === MAP_TOOLS.MEASURE_AREA
                                    ? getDrawingAreaM2(drawing)
                                    : drawing.rawValue,
                                  drawing.unit
                                )
                              : drawing.result}
                          </span>
                          {(drawing.type === MAP_TOOLS.MEASURE ||
                            drawing.type === MAP_TOOLS.MEASURE_AREA) &&
                            drawing.rawValue !== undefined && (
                              <select
                                value={drawing.unit || ""}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  changeUnit(drawing.id, e.target.value);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="text-xs px-2 py-1 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer"
                              >
                                {drawing.type === MAP_TOOLS.MEASURE ? (
                                  <>
                                    <option value="km">km</option>
                                    <option value="m">m</option>
                                    <option value="mi">mi</option>
                                    <option value="ft">ft</option>
                                  </>
                                ) : (
                                  <>
                                    <option value="m²">m²</option>
                                    <option value="km²">km²</option>
                                    <option value="ha">ha</option>
                                    <option value="acres">acres</option>
                                  </>
                                )}
                              </select>
                            )}
                        </div>
                      )}
                      {!drawing.result && (
                        <p className="text-xs text-gray-400">
                          Desenho Finalizado
                        </p>
                      )}
                    </>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {onReportDrawing &&
                    drawing.type === MAP_TOOLS.MEASURE_AREA && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onReportDrawing(drawing.id);
                        }}
                        className="p-1.5 hover:bg-primary/10 rounded-lg transition-colors"
                        title="Gerar relatório"
                      >
                        <FileText className="w-4 h-4 text-primary" />
                      </button>
                    )}
                  {onExportArea &&
                    drawing.type === MAP_TOOLS.MEASURE_AREA &&
                    (() => {
                      const areaM2 = getDrawingAreaM2(drawing);
                      const isExportable = isAreaExportable(areaM2);
                      const areaHa = formatAreaNumber(
                        areaM2 /
                          AREA_CONVERSION_FACTORS.SQUARE_METERS_PER_HECTARE,
                        "ha"
                      );
                      const blockedMessage = `Exportação bloqueada: a área selecionada tem ${areaHa} ha e o limite é de ${AREA_EXPORT_MAX_AREA_HA} ha. Reduza a área para exportar.`;

                      return (
                        // O wrapper recebe o hover porque botões desabilitados não emitem eventos de mouse.
                        <span className="relative group/export inline-flex">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onExportArea(drawing.id);
                            }}
                            disabled={
                              exportingDrawingId === drawing.id || !isExportable
                            }
                            className="p-1.5 hover:bg-primary/10 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label={
                              isExportable
                                ? "Exportar pacote (KML + Excel)"
                                : blockedMessage
                            }
                          >
                            {exportingDrawingId === drawing.id ? (
                              <Loader2 className="w-4 h-4 text-primary animate-spin" />
                            ) : (
                              <FileArchive className="w-4 h-4 text-primary" />
                            )}
                          </button>
                          <span
                            role="tooltip"
                            className={cn(
                              "pointer-events-none absolute bottom-full right-0 mb-1.5 z-[10000]",
                              "rounded-lg bg-gray-800 px-2.5 py-1.5 text-[11px] font-medium leading-snug text-white shadow-lg",
                              "opacity-0 transition-opacity duration-150 group-hover/export:opacity-100",
                              isExportable ? "whitespace-nowrap" : "w-56"
                            )}
                          >
                            {isExportable
                              ? "Exportar pacote (KML + Excel)"
                              : blockedMessage}
                          </span>
                        </span>
                      );
                    })()}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleZoomToLayer(drawing);
                    }}
                    className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                    title="Dar zoom na marcação"
                  >
                    <ZoomIn className="w-4 h-4 text-primary" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleDrawingVisibility(drawing.id);
                    }}
                    className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    {drawing.isVisible ? (
                      <Eye className="w-4 h-4 text-gray-500" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-gray-300" />
                    )}
                  </button>
                  <button
                    onClick={(e) => handleDeleteDrawing(e, drawing.id)}
                    className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {importedLayers.length > 0 && (
        <div className="flex flex-col">
          <div className="flex items-center justify-between px-5 py-3 bg-green-50 border-b border-green-100">
            <h4 className="text-sm font-semibold text-green-700">
              Camadas Importadas ({importedLayers.length})
            </h4>
          </div>
          <div className="px-4 py-3">
            {importedLayers.map((layer) => (
              <div
                key={layer.id}
                className={cn(
                  "flex items-start gap-3 p-3 mb-2 rounded-xl cursor-pointer",
                  "transition-all duration-200",
                  selectedImportedLayerId === layer.id
                    ? "bg-green-50 border border-green-200"
                    : "",
                  !selectedImportedLayerId ||
                    selectedImportedLayerId !== layer.id
                    ? layer.isVisible
                      ? "bg-gray-50 hover:bg-gray-100 border border-transparent"
                      : "bg-gray-100 opacity-50 border border-transparent"
                    : ""
                )}
                onClick={() => handleImportedLayerClick(layer.id)}
                onMouseEnter={() => handleImportedLayerMouseEnter(layer.id)}
                onMouseLeave={() => handleImportedLayerMouseLeave(layer.id)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-green-100 text-green-700 uppercase">
                      {layer.format}
                    </span>
                    <span
                      className="text-sm font-medium text-gray-800 truncate"
                      title={layer.name}
                    >
                      {layer.name}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400">
                    {layer.featureCount} feições
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleZoomToImportedLayer(layer.id);
                    }}
                    className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    <ZoomIn className="w-4 h-4 text-green-700" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleImportedLayerVisibility(layer.id);
                    }}
                    className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    {layer.isVisible ? (
                      <Eye className="w-4 h-4 text-gray-500" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-gray-300" />
                    )}
                  </button>
                  <button
                    onClick={(e) => handleDeleteImportedLayer(e, layer.id)}
                    className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isFinished && !isConfirmed && (
        <div className="flex flex-col px-5 py-5 border-t border-gray-100 bg-blue-50/50">
          <div className="flex items-center gap-2 mb-3">
            <div className="text-primary">
              {activeFunction && getDrawingIcon(activeFunction as DrawingType)}
            </div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-widest">
              {activeFunction === MAP_TOOLS.MEASURE && "Distância Total"}
              {activeFunction === MAP_TOOLS.MEASURE_AREA && "Área Total"}
              {activeFunction === MAP_TOOLS.LOCATION &&
                "Localização Selecionada"}
              {activeFunction === MAP_TOOLS.DRAW && "Desenho Finalizado"}
            </h4>
          </div>
          {measureResult && (
            <Typography variant="body2" className="text-primary font-bold mb-4">
              {measureResult}
            </Typography>
          )}
          <div className="flex gap-2">
            <button
              ref={confirmRef}
              onClick={handleConfirmMeasure}
              onKeyDown={handleConfirmKeyDown}
              className="flex items-center justify-center gap-2 flex-1 px-4 py-3 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors font-semibold text-sm"
            >
              <Check className="w-4 h-4" />
              Confirmar
            </button>
            <button
              ref={discardRef}
              onClick={cancelMeasure}
              onKeyDown={handleDiscardKeyDown}
              className="flex-1 px-4 py-3 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition-colors font-medium text-sm"
            >
              Descartar
            </button>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};
